import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { Workbook, Worksheet } from 'exceljs';
import * as fs from 'file-saver';
import { NgDialogAnimationService } from 'ng-dialog-animation';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription, catchError, finalize, of } from 'rxjs';
import {
  HrAlertService,
  HrHttpService,
  ManpowerBudgetDashboard,
  ManpowerBudgetDashboardFilters,
} from '../hr-http.service';
import { MrfFilterDialogState } from '../mrf-dashboard-state.service';
import { MrfFilterDialogComponent } from '../mrf-filter-dialog/mrf-filter-dialog.component';
import { canAccessHrmsBudgetReport } from '../mrf-view-options';

export interface MrfBudgetReportRow {
  sno: number;
  state: string;
  city: string;
  zone: string;
  subZone: string;
  campus: string;
  deanName: string;
  teachingType: string;
  department: string;
  designation: string;
  subject: string;
  budgetedPositions: number;
  filledPositions: number;
  positionBalance: number;
  allocatedBudget: number;
  utilizedBudget: number;
  budgetBalance: number;
  status: 'Available' | 'Exceeded' | 'No Budget';
}

@Component({
  selector: 'app-mrf-budget-report',
  templateUrl: './mrf-budget-report.component.html',
  styleUrls: ['./mrf-budget-report.component.scss'],
})
export class MrfBudgetReportComponent implements OnInit, OnDestroy {
  readonly icons = {
    update: 'assets/image/mrf-budget-report/update.svg',
    funnel: 'assets/image/mrf-budget-report/funnel.svg',
    usersViolet: 'assets/image/mrf-budget-report/users-violet.svg',
    usersGreen: 'assets/image/mrf-budget-report/users-green.svg',
    usersOrange: 'assets/image/mrf-budget-report/users-orange.svg',
    arrowDown: 'assets/image/mrf-budget-report/arrow-down.svg',
    rupee: 'assets/image/mrf-budget-report/indian-rupee.svg',
    wallet: 'assets/image/mrf-budget-report/wallet.svg',
    barChart: 'assets/image/mrf-budget-report/bar-chart.svg',
    mapPin: 'assets/image/mrf-budget-report/map-pin.svg',
    blocks: 'assets/image/mrf-budget-report/blocks.svg',
    dashboard: 'assets/image/mrf-budget-report/layout-dashboard.svg',
    markerGreen: 'assets/image/mrf-budget-report/marker-green.svg',
    markerRed: 'assets/image/mrf-budget-report/marker-red.svg',
    chartNetwork: 'assets/image/mrf-budget-report/chart-network.svg',
    search: 'assets/image/mrf-budget-report/search.svg',
  } as const;

  searchText = '';
  filterCount = 0;
  lastUpdated = '21 Sep 2026 10:30 AM';
  private appliedCustomizeRows: Array<{ filterBy: string | null; value: unknown }> = [];
  private filterDialogState: MrfFilterDialogState | null = null;
  private readonly subs = new Subscription();
  private pendingLoadCount = 0;

  constructor(
    private hrHttp: HrHttpService,
    private hrAlert: HrAlertService,
    private router: Router,
    private filterDialog: NgDialogAnimationService,
    private spinner: NgxSpinnerService
  ) {}

  private beginLoading(): void {
    this.pendingLoadCount += 1;
    if (this.pendingLoadCount === 1) {
      void this.spinner.show();
    }
  }

  private endLoading(): void {
    this.pendingLoadCount = Math.max(0, this.pendingLoadCount - 1);
    if (this.pendingLoadCount === 0) {
      void this.spinner.hide();
    }
  }

  openCustomizeFilterDialog(): void {
    const isNarrow = typeof window !== 'undefined' && window.innerWidth < 760;
    const dialogRef = this.filterDialog.open(MrfFilterDialogComponent, {
      width: isNarrow ? '100vw' : '52vw',
      minWidth: isNarrow ? '100vw' : '580px',
      height: '100%',
      position: { right: '0' },
      maxWidth: '100%',
      animation: { to: 'left' },
      autoFocus: false,
      disableClose: true,
      data: {
        mode: 'customize',
        skipDashboardState: true,
        savedState: this.filterDialogState,
      },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.status === 'search') {
        this.appliedCustomizeRows = data.customizeRows ?? [];
        this.filterCount = this.appliedCustomizeRows.length;
        this.filterDialogState = data.dialogState ?? this.filterDialogState;
        this.loadDashboard();
        return;
      }
      if (data?.status === 'reset') {
        this.appliedCustomizeRows = [];
        this.filterCount = 0;
        this.filterDialogState = null;
        this.loadDashboard();
      }
    });
  }

  ngOnInit(): void {
    this.beginLoading();
    this.subs.add(
      this.hrHttp
        .getUserRoles()
        .pipe(
          catchError(() => of(null)),
          finalize(() => this.endLoading())
        )
        .subscribe((roles) => {
          const permissions = roles
            ? this.hrHttp.setMrfHrmsPermissionsFromResponse(roles)
            : this.hrHttp.getMrfHrmsPermissions();
          if (!canAccessHrmsBudgetReport(permissions)) {
            this.hrAlert.show('No permission to access budget report.', 'Warning');
            void this.router.navigate(['/hrDetails/mrfDashboard']);
            return;
          }
          this.loadDashboard();
        })
    );
  }

  private loadDashboard(): void {
    this.beginLoading();
    this.subs.add(
      this.hrHttp
        .getManpowerBudgetDashboard(this.buildDashboardFilters())
        .pipe(
          catchError(() => of(null)),
          finalize(() => this.endLoading())
        )
        .subscribe((res) => {
          if (!res) {
            this.hrAlert.show('Unable to load manpower budget report.', 'Warning');
            return;
          }
          this.bindDashboard(this.hrHttp.parseManpowerBudgetDashboard(res));
        })
    );
  }

  private buildDashboardFilters(): ManpowerBudgetDashboardFilters {
    const payload = this.hrHttp.emptyManpowerBudgetDashboardFilters();
    for (const filter of this.appliedCustomizeRows) {
      const value = filter.value;
      const row = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      switch (filter.filterBy) {
        case 'los':
          payload.location = this.filterText(row, 'name', 'location', 'los', 'losName') || null;
          break;
        case 'state':
          payload.stateId = this.filterNum(row, 'stateId', 'id');
          break;
        case 'city':
          payload.cityId = this.filterNum(row, 'cityId', 'id');
          break;
        case 'zone':
          payload.zone = this.filterText(row, 'zoneName', 'zone', 'name') || null;
          break;
        case 'subZone':
          payload.subZone = this.filterText(row, 'subZone', 'subZoneName', 'name') || null;
          break;
        case 'campus':
          payload.campusId = this.filterNum(row, 'campusId', 'id');
          break;
        case 'deanName':
          payload.deanName = this.filterText(row, 'deanName', 'name') || null;
          break;
        default:
          break;
      }
    }
    return payload;
  }

  private filterText(row: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const value = row[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private filterNum(row: Record<string, unknown>, ...keys: string[]): number | null {
    for (const key of keys) {
      const n = Number(row[key]);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return null;
  }

  private bindDashboard(data: ManpowerBudgetDashboard): void {
    this.kpis = {
      totalBudgeted: data.totalBudgetedPositions,
      filled: data.filledPositions,
      positionBalance: data.positionBalance,
      totalBudget: data.totalBudgetAmount,
      utilizedBudget: data.utilizedBudget,
      budgetBalance: data.balanceBudget,
      totalBudgetCr: data.totalBudgetAmount / 10000000,
      utilizedBudgetCr: data.utilizedBudget / 10000000,
      budgetBalanceCr: data.balanceBudget / 10000000,
    };
    this.positionStatus = {
      within: data.positionWithinLimit,
      exceeded: data.positionExceeded,
    };
    this.budgetStatus = {
      within: data.budgetWithinLimit,
      exceeded: data.budgetExceeded,
    };
    this.rows = data.details.map((row) => ({
      sno: row.sno,
      state: row.stateName,
      city: row.cityName,
      zone: row.zone,
      subZone: row.subZone,
      campus: row.campusName,
      deanName: row.deanName,
      teachingType: row.empTypeName,
      department: row.department,
      designation: row.designation,
      subject: row.subjectName,
      budgetedPositions: row.budgetedPositions,
      filledPositions: row.filledPositions,
      positionBalance: row.positionBalance,
      allocatedBudget: row.allocatedBudget,
      utilizedBudget: row.utilizedBudget,
      budgetBalance: row.balanceBudget,
      status: this.mapBudgetStatus(row.budgetStatus),
    }));
    this.lastUpdated = this.formatLastUpdated(new Date());
  }

  private mapBudgetStatus(status: string): MrfBudgetReportRow['status'] {
    const value = status.trim().toLowerCase();
    if (value.includes('exceed')) {
      return 'Exceeded';
    }
    if (value.includes('no budget')) {
      return 'No Budget';
    }
    return 'Available';
  }

  private formatLastUpdated(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day} ${month} ${year} ${time}`;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  kpis = {
    totalBudgeted: 0,
    filled: 0,
    positionBalance: 0,
    totalBudget: 0,
    utilizedBudget: 0,
    budgetBalance: 0,
    totalBudgetCr: 0,
    utilizedBudgetCr: 0,
    budgetBalanceCr: 0,
  };

  positionStatus = { within: 0, exceeded: 0 };
  budgetStatus = { within: 0, exceeded: 0 };

  rows: MrfBudgetReportRow[] = [];
  private gridApi: GridApi<MrfBudgetReportRow> | null = null;

  readonly overlayNoRowsTemplate =
    '<span class="ag-overlay-no-rows-center">No manpower budget records found.</span>';

  readonly defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    minWidth: 110,
    suppressHeaderMenuButton: true,
    menuTabs: [],
    headerClass: 'mbr__text-header',
    cellClass: 'mbr__text',
    cellStyle: { textAlign: 'center' },
  };

  readonly autoGroupColumnDef: ColDef<MrfBudgetReportRow> = {
    headerName: 'Campus',
    minWidth: 220,
    pinned: 'left',
    headerClass: 'mbr__text-header',
    cellClass: 'mbr__text',
    cellStyle: { textAlign: 'center' },
    cellRendererParams: {
      suppressCount: false,
    },
  };

  private locationValueGetter =
    (field: 'state' | 'city' | 'zone' | 'subZone') =>
    (params: ValueGetterParams<MrfBudgetReportRow>): string => {
      if (params.node?.rowPinned) {
        return '';
      }
      if (params.data) {
        return String(params.data[field] ?? '').trim();
      }
      const firstLeaf = params.node?.allLeafChildren?.find((node) => !!node.data)?.data;
      return String(firstLeaf?.[field] ?? '').trim();
    };

  private numericCellClass(params: { value?: unknown }): string[] {
    const classes = ['mbr__num'];
    if (Number(params.value) < 0) {
      classes.push('mbr__neg');
    }
    return classes;
  }

  readonly columnDefs: ColDef<MrfBudgetReportRow>[] = [
    { field: 'campus', headerName: 'Campus', rowGroup: true, hide: true },
    {
      field: 'state',
      headerName: 'State',
      minWidth: 120,
      valueGetter: this.locationValueGetter('state'),
    },
    {
      field: 'city',
      headerName: 'City',
      minWidth: 110,
      valueGetter: this.locationValueGetter('city'),
    },
    {
      colId: 'zoneName',
      field: 'zone',
      headerName: 'Zone',
      minWidth: 140,
      valueGetter: this.locationValueGetter('zone'),
    },
    {
      colId: 'subZoneName',
      field: 'subZone',
      headerName: 'Sub Zone',
      minWidth: 150,
      valueGetter: this.locationValueGetter('subZone'),
    },
    { field: 'deanName', headerName: 'Dean Name', minWidth: 150 },
    { field: 'teachingType', headerName: 'Teaching Type', minWidth: 130 },
    { field: 'department', headerName: 'Department', minWidth: 140 },
    { field: 'designation', headerName: 'Designation', minWidth: 160 },
    { field: 'subject', headerName: 'Subject', minWidth: 140 },
    {
      field: 'budgetedPositions',
      headerName: 'Budgeted Positions',
      minWidth: 150,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'filledPositions',
      headerName: 'Filled Positions',
      minWidth: 140,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'positionBalance',
      headerName: 'Position Balance',
      minWidth: 140,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'allocatedBudget',
      headerName: 'Allocated Budget (₹)',
      minWidth: 160,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
      valueFormatter: (params) => this.formatGridInr(params.value),
    },
    {
      field: 'utilizedBudget',
      headerName: 'Utilized Budget (₹)',
      minWidth: 150,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
      valueFormatter: (params) => this.formatGridInr(params.value),
    },
    {
      field: 'budgetBalance',
      headerName: 'Balance Budget',
      minWidth: 140,
      aggFunc: 'sum',
      headerClass: 'mbr__num-header',
      cellClass: (params) => this.numericCellClass(params),
      cellStyle: { textAlign: 'right' },
      valueFormatter: (params) => this.formatGridInr(params.value),
    },
    {
      field: 'status',
      headerName: 'Budget Status',
      minWidth: 130,
      cellRenderer: (params: ICellRendererParams<MrfBudgetReportRow>) => this.renderStatus(params),
    },
  ];

  onGridReady(event: GridReadyEvent<MrfBudgetReportRow>): void {
    this.gridApi = event.api;
  }

  get positionBarMax(): number {
    return this.chartMax(this.kpis.totalBudgeted, this.kpis.filled, this.kpis.positionBalance);
  }

  get budgetBarMax(): number {
    return this.chartMax(this.kpis.totalBudgetCr, this.kpis.utilizedBudgetCr, this.kpis.budgetBalanceCr);
  }

  get positionAxisTicks(): number[] {
    return this.axisTicks(this.positionBarMax);
  }

  get budgetAxisTicks(): number[] {
    return this.axisTicks(this.budgetBarMax);
  }

  private chartMax(...values: number[]): number {
    const max = Math.max(0, ...values);
    if (max <= 0) {
      return 1;
    }
    const step = max >= 100 ? 100 : max >= 10 ? 5 : 1;
    return Math.ceil(max / step) * step;
  }

  private axisTicks(max: number): number[] {
    return [max, (max * 2) / 3, max / 3, 0].map((value) => Math.round(value * 100) / 100);
  }

  get filteredRows(): MrfBudgetReportRow[] {
    return this.rows.filter((row) => this.matchesCustomizeFilters(row));
  }

  get pinnedBottomRowData(): Array<Partial<MrfBudgetReportRow>> {
    const totals = this.totals;
    return [
      {
        campus: 'Total',
        budgetedPositions: totals.budgeted,
        filledPositions: totals.filled,
        positionBalance: totals.balance,
        allocatedBudget: totals.allocated,
        utilizedBudget: totals.utilized,
        budgetBalance: totals.budgetBalance,
      },
    ];
  }

  private matchesCustomizeFilters(row: MrfBudgetReportRow): boolean {
    if (!this.appliedCustomizeRows.length) {
      return true;
    }
    return this.appliedCustomizeRows.every((filter) => {
      const expected = this.customizeFilterLabel(filter.filterBy, filter.value);
      if (!expected) {
        return true;
      }
      const actual = this.rowValueForFilter(row, filter.filterBy);
      if (!actual) {
        return true;
      }
      return actual.toLowerCase() === expected.toLowerCase();
    });
  }

  private rowValueForFilter(row: MrfBudgetReportRow, filterBy: string | null): string {
    switch (filterBy) {
      case 'state':
        return row.state;
      case 'city':
        return row.city;
      case 'zone':
        return row.zone;
      case 'subZone':
        return row.subZone;
      case 'deanName':
        return row.deanName;
      case 'campus':
        return row.campus;
      default:
        return '';
    }
  }

  private customizeFilterLabel(filterBy: string | null, value: unknown): string {
    if (!value || typeof value !== 'object') {
      return String(value ?? '').trim();
    }
    const row = value as Record<string, unknown>;
    switch (filterBy) {
      case 'los':
        return String(row['name'] ?? row['location'] ?? row['los'] ?? row['losName'] ?? '').trim();
      case 'state':
        return String(row['name'] ?? row['stateName'] ?? '').trim();
      case 'city':
        return String(row['name'] ?? row['cityName'] ?? '').trim();
      case 'zone':
        return String(row['zoneName'] ?? row['name'] ?? '').trim();
      case 'subZone':
        return String(row['subZone'] ?? row['subZoneName'] ?? row['name'] ?? '').trim();
      case 'deanName':
        return String(row['deanName'] ?? row['name'] ?? '').trim();
      case 'campus':
        return String(row['campusName'] ?? row['name'] ?? '').trim();
      default:
        return String(row['name'] ?? '').trim();
    }
  }

  get totals() {
    return this.filteredRows.reduce(
      (acc, row) => {
        acc.budgeted += row.budgetedPositions;
        acc.filled += row.filledPositions;
        acc.balance += row.positionBalance;
        acc.allocated += row.allocatedBudget;
        acc.utilized += row.utilizedBudget;
        acc.budgetBalance += row.budgetBalance;
        return acc;
      },
      { budgeted: 0, filled: 0, balance: 0, allocated: 0, utilized: 0, budgetBalance: 0 }
    );
  }

  get positionPct(): number {
    const total = this.positionStatus.within + this.positionStatus.exceeded;
    return total ? Math.round((this.positionStatus.within / total) * 100) : 0;
  }

  get budgetPct(): number {
    const total = this.budgetStatus.within + this.budgetStatus.exceeded;
    return total ? Math.round((this.budgetStatus.within / total) * 100) : 0;
  }

  get positionDonutStyle(): Record<string, string> {
    return { background: `conic-gradient(#49c99a 0 ${this.positionPct}%, #f04438 ${this.positionPct}% 100%)` };
  }

  get budgetDonutStyle(): Record<string, string> {
    return { background: `conic-gradient(#49c99a 0 ${this.budgetPct}%, #f04438 ${this.budgetPct}% 100%)` };
  }

  barHeight(value: number, max: number): string {
    const plotPx = 110;
    if (!max || !Number.isFinite(value) || value <= 0) {
      return '4px';
    }
    return `${Math.max(4, (value / max) * plotPx)}px`;
  }

  formatGridInr(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return '';
    }
    return this.formatCompactInr(n);
  }

  private renderStatus(params: ICellRendererParams<MrfBudgetReportRow>): string {
    if (params.node?.group || params.node?.rowPinned) {
      return '';
    }
    const status = params.value as MrfBudgetReportRow['status'] | undefined;
    if (!status) {
      return '';
    }
    return `<span class="mbr__badge ${this.statusClass(status)}">${status}</span>`;
  }

  formatInr(value: number): string {
    const abs = Math.abs(value);
    const formatted = abs.toLocaleString('en-IN');
    return value < 0 ? `-${formatted}` : formatted;
  }

  formatCompactInr(value: number): string {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const fmt = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);
    if (abs >= 10000000) {
      return `${sign}₹ ${fmt(abs / 10000000)} Cr`;
    }
    if (abs >= 100000) {
      return `${sign}₹ ${fmt(abs / 100000)} L`;
    }
    if (abs >= 1000) {
      return `${sign}₹ ${fmt(abs / 1000)} K`;
    }
    return `${sign}₹ ${fmt(abs)}`;
  }

  statusClass(status: MrfBudgetReportRow['status']): string {
    if (status === 'Available') {
      return 'mbr__badge--available';
    }
    if (status === 'Exceeded') {
      return 'mbr__badge--exceeded';
    }
    return 'mbr__badge--none';
  }

  exportCsv(): void {
    const rows = this.excelExportRows();
    if (!rows.length) {
      this.hrAlert.show('No data available to export.', 'Warning');
      return;
    }
    void this.downloadBudgetExcel(rows);
  }

  private excelExportRows(): MrfBudgetReportRow[] {
    const rows: MrfBudgetReportRow[] = [];
    if (this.gridApi) {
      this.gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.data && !node.group) {
          rows.push(node.data);
        }
      });
      return rows;
    }
    return [...this.filteredRows];
  }

  private async downloadBudgetExcel(rows: MrfBudgetReportRow[]): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Manpower Budget Report');
    const lastColLetter = this.excelColumnLetter(Math.max(this.excelExportColumns.length, 3));
    const now = new Date();
    const reportDateTime =
      now.toLocaleDateString('en-GB') + ' ' + now.toLocaleTimeString('en-GB');

    worksheet.mergeCells('A1:B4');
    worksheet.mergeCells(`C1:${lastColLetter}1`);
    worksheet.mergeCells(`C2:${lastColLetter}2`);
    worksheet.mergeCells(`C3:${lastColLetter}3`);
    worksheet.mergeCells(`C4:${lastColLetter}4`);

    [1, 2, 3, 4].forEach((rowNumber) => {
      worksheet.getRow(rowNumber).height = 18;
    });

    await this.addBudgetExcelLogo(workbook, worksheet);

    worksheet.getCell('C1').value = 'Sri Chaitanya Educational Institutions';
    worksheet.getCell('C2').value = 'Manpower Budget Report';
    worksheet.getCell('C3').value = `Records : ${rows.length}`;
    worksheet.getCell('C4').value = `Date : ${reportDateTime}`;

    ['C1', 'C2', 'C3', 'C4'].forEach((cell) => {
      worksheet.getCell(cell).alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
    });

    worksheet.getCell('C1').font = {
      bold: true,
      size: 16,
      underline: true,
      color: { argb: 'FF8C00' },
    };
    worksheet.getCell('C2').font = { bold: true, size: 13 };
    worksheet.getCell('C3').font = { bold: true, size: 12 };
    worksheet.getCell('C4').font = { bold: true, size: 12 };

    worksheet.addRow([]);
    worksheet.addRow([]);

    const headers = this.excelExportColumns.map((col) => col.header);
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
        bottom: { style: 'thin' },
      };
    });

    rows.forEach((row) => {
      const dataRow = worksheet.addRow(
        this.excelExportColumns.map((col) => this.excelCellValue(col.field, row))
      );
      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
          bottom: { style: 'thin' },
        };
        const field = this.excelExportColumns[colNumber - 1]?.field ?? '';
        cell.alignment = {
          horizontal: this.isExcelAmountField(field) ? 'right' : 'left',
          vertical: 'middle',
          wrapText: false,
        };
      });
    });

    this.fitBudgetExcelColumns(worksheet, this.excelExportColumns.length, headerRow.number);
    worksheet.getColumn(1).width = Math.max(Number(worksheet.getColumn(1).width ?? 0), 14);
    worksheet.getColumn(2).width = Math.max(Number(worksheet.getColumn(2).width ?? 0), 14);

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(blob, `ManpowerBudgetReport_${now.toISOString().slice(0, 10)}.xlsx`);
        this.hrAlert.show('Excel downloaded successfully.', 'Success');
      })
      .catch((error) => {
        console.error(error);
        this.hrAlert.show('Failed to download Excel file.', 'Error');
      });
  }

  private readonly excelExportColumns: Array<{ field: keyof MrfBudgetReportRow; header: string }> = [
    { field: 'state', header: 'State' },
    { field: 'city', header: 'City' },
    { field: 'zone', header: 'Zone' },
    { field: 'subZone', header: 'Sub Zone' },
    { field: 'campus', header: 'Campus' },
    { field: 'deanName', header: 'Dean Name' },
    { field: 'teachingType', header: 'Teaching Type' },
    { field: 'department', header: 'Department' },
    { field: 'designation', header: 'Designation' },
    { field: 'subject', header: 'Subject' },
    { field: 'budgetedPositions', header: 'Budgeted Positions' },
    { field: 'filledPositions', header: 'Filled Positions' },
    { field: 'positionBalance', header: 'Position Balance' },
    { field: 'allocatedBudget', header: 'Allocated Budget (₹)' },
    { field: 'utilizedBudget', header: 'Utilized Budget (₹)' },
    { field: 'budgetBalance', header: 'Balance Budget' },
    { field: 'status', header: 'Budget Status' },
  ];

  private isExcelAmountField(field: string): boolean {
    return field === 'allocatedBudget' || field === 'utilizedBudget' || field === 'budgetBalance';
  }

  private excelCellValue(field: keyof MrfBudgetReportRow, row: MrfBudgetReportRow): string | number {
    const value = row[field];
    if (this.isExcelAmountField(field)) {
      return this.formatInr(Number(value ?? 0));
    }
    if (value == null || String(value).trim() === '') {
      return '';
    }
    return value as string | number;
  }

  private excelColumnLetter(columnNumber: number): string {
    let n = columnNumber;
    let letter = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      letter = String.fromCharCode(65 + rem) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter || 'A';
  }

  private fitBudgetExcelColumns(
    worksheet: Worksheet,
    columnCount: number,
    tableStartRow: number
  ): void {
    for (let index = 1; index <= columnCount; index++) {
      const column = worksheet.getColumn(index);
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        if (rowNumber < tableStartRow) {
          return;
        }
        const text = cell.value == null ? '' : String(cell.value);
        maxLength = Math.max(maxLength, text.length);
      });
      const minWidth = index === 1 ? 18 : 12;
      column.width = Math.min(50, Math.max(minWidth, maxLength + 3));
    }
  }

  private async addBudgetExcelLogo(workbook: Workbook, worksheet: Worksheet): Promise<void> {
    try {
      const response = await fetch('assets/image/budget-report/excel-logo.png');
      if (!response.ok) {
        return;
      }
      const buffer = await response.arrayBuffer();
      const logoId = workbook.addImage({
        buffer,
        extension: 'png',
      });
      worksheet.addImage(logoId, {
        tl: { col: 0, row: 0 },
        br: { col: 2, row: 4 },
        editAs: 'oneCell',
      } as Parameters<Worksheet['addImage']>[1]);
    } catch {
      /* logo is optional */
    }
  }
}
